import React, { FunctionComponent, useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import classNames from 'classnames';
import styles from './styles.module.scss';
import playerStyles from 'components/Player/styles.module.scss';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { setChatVisible } from 'features/chat/chatSlice';
import { useDispatch } from 'react-redux';
import { Pill } from 'components/Pill';
import { useAudio } from 'features/audio/audio';
import { setMiniPlayerVisible } from 'features/channel/channelSlice';
import { EventDate } from 'features/events/EventDate';
import { RecordingDate } from 'features/recordings/components/RecordingDate';
import { useCurrentEvent } from 'features/events/eventHooks';
import { useCurrentRecording } from 'features/recordings/recordingHooks';
import { useMediaQuery } from 'react-responsive';
import { Icon } from '../Icons';
import { CardImage } from 'components/Images/CardImage';
import { FullImage } from 'components/Images/FullImage';
import { LiveNow } from 'components/LiveNow';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useNativeEvents } from 'hooks/useNativeEvents';
import { cloudflareUrl } from 'utilities/cloudflareImageResizer';

interface CardProps {
  id: string;
  title: string;
  url?: string | null;
  artworkKey?: string | null;
  artworkMode?: string | undefined;
  featured?: boolean;
  isPrivate?: boolean;
  isActive?: boolean;
  isFeatured?: boolean;
  type: 'event' | 'recording';
}

export const Card: FunctionComponent<CardProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stop, unlockAudio } = useAudio();
  const playing = useSelector((state: RootState) => state.audio.playing);
  const eventIsLive = props.isActive;
  const event = useCurrentEvent();
  const recording = useCurrentRecording();
  const isMobile = useMediaQuery({ query: '(max-width: 739px)' });
  const { isNativeAppWebview } = useContext(UserAgentContext);
  const { nativeUrlSchemeLink } = useNativeEvents();
  const channel = useSelector(
    (state: RootState) => state.channel.channelDetails
  );

  const onClickEvent = (e: React.MouseEvent<HTMLElement>) => {
    if (!eventIsLive && playing) {
      e.preventDefault();
      displayEventConfirmMessage();
    } else {
      dispatchEvents();
    }
  };

  const onClickRecording = (e: React.MouseEvent<HTMLElement>) => {
    if (event && playing) {
      e.preventDefault();
      displayEventConfirmMessage();
    } else {
      dispatchEvents();
    }
  };

  const displayEventConfirmMessage = () => {
    if (confirm('You are going to leave the current event, are you sure?')) {
      dispatchEvents();
      navigate(`${props.url}`);
    }
  };

  const dispatchEvents = () => {
    unlockAudio();
    dispatch(setMiniPlayerVisible(false));
    dispatch(setChatVisible(isMobile ? false : true));

    if (
      (event && event.id !== props.id) ||
      (recording && recording.id !== props.id)
    ) {
      stop(true);
    }
  };

  const featuredText =
    props.type === 'event' ? 'Next live event' : 'Latest recording';

  const isPlaying = () => {
    if (
      playing &&
      ((event && event.id === props.id && eventIsLive) ||
        (recording && recording.id === props.id))
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isPlayable =
    (props.type === 'recording' || eventIsLive) && !isPlaying();

  const cardContent = (
    <>
      {props.isFeatured ? (
        <FullImage
          artworkKey={props.artworkKey}
          artworkMode={props.artworkMode}
          className={styles['card__image']}
          type={props.type}
        />
      ) : (
        <CardImage
          artworkKey={props.artworkKey}
          artworkMode={props.artworkMode}
          className={styles['card__image']}
          type={props.type}
        />
      )}
      <div className={styles['card__content']}>
        <h2 className={classNames(styles['card__title'])}>{props.title}</h2>
        {props.type === 'event' ? (
          <EventDate eventId={props.id} cssClass={styles['card__meta']} />
        ) : (
          <RecordingDate
            recordingId={props.id}
            cssClass={styles['card__meta']}
          />
        )}
      </div>
      {isPlayable && (
        <div
          className={classNames(
            styles['card__play'],
            playerStyles['player__button'],
            {
              [playerStyles['player__button--small']]:
                isMobile || !props.isFeatured,
              [playerStyles['player__button--large']]: props.isFeatured,
              [playerStyles['player__button--playing']]: isPlaying(),
              [styles['card__play--featured']]: props.isFeatured
            }
          )}
        >
          <Icon
            hidden={true}
            icon={isPlaying() ? 'pause' : 'play'}
            label={isPlaying() ? 'Pause audio' : 'Play audio'}
          />
        </div>
      )}

      <div className={styles['card__status']}>
        {props.isPrivate && (
          <Pill label={'Private'} type={'success'} icon={'lock'} />
        )}
        <LiveNow isLive={eventIsLive} />
        {props.isFeatured && !eventIsLive && (
          <Pill label={featuredText} type={'success'} />
        )}
      </div>
    </>
  );

  const CardLinkWrapper: FunctionComponent = ({ children }) => {
    if (!props.url) {
      return null;
    }
    if (isNativeAppWebview) {
      return (
        <a
          href={nativeUrlSchemeLink('channel', props.type, {
            url: `${
              window.location.protocol + '//' + window.location.host + props.url
            }`,
            channel_url: window.location.origin,
            channel_name: channel?.username,
            channel_theme_color: channel?.themeColor,
            channel_logo_url: cloudflareUrl(channel?.artworkKey, {
              width: 100,
              quality: 80
            })
          })}
          className={styles['card__link']}
        >
          {children}
        </a>
      );
    } else {
      return (
        <Link
          to={props.url}
          state={{
            prevPath: location.pathname,
            animate: true,
            autoPlay: isPlayable
          }}
          className={styles['card__link']}
          onClick={props.type === 'event' ? onClickEvent : onClickRecording}
        >
          {children}
        </Link>
      );
    }
  };

  return (
    <motion.article
      className={classNames(styles['card'], {
        [styles['card--with-image']]:
          (props.artworkKey && props.artworkMode !== 'theme') ||
          props.artworkMode === 'channel',
        [styles['card--event']]: props.featured,
        [styles['card--featured']]: props.isFeatured
      })}
    >
      {props.url ? (
        <CardLinkWrapper>{cardContent}</CardLinkWrapper>
      ) : (
        { cardContent }
      )}
    </motion.article>
  );
};
